import { sortByCreatedAt } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import {
  useFileCollectionsFromParentId,
  useFilesFromFileCollectionId,
} from '@witmetrics/data-store';
import { Tab, type TabProps } from '@/components/TabsBlock';
import FileCollectionCrumbs, {
  type FileCollectionPath,
} from '@/components/FileCollectionCrumbs';
import { FileItem, FileCollectionItem } from '@/components/FileCollection';
import { classes } from './utils';

export interface FilesTabProps extends TabProps {
  activeCollectionId: number;
  baseCollectionId: number;
  activeFileId?: number;
  crumbs: FileCollectionPath[];
  onSelectCollection: (collectionId: number) => void;
  onSelectCrumb: (collectionId: number | undefined) => void;
  onSelectFile: (fileId: number) => void;
}

export default function FilesTab({
  name,
  label,
  activeCollectionId,
  baseCollectionId,
  activeFileId,
  crumbs,
  onSelectCollection,
  onSelectCrumb,
  onSelectFile,
}: FilesTabProps) {
  const collections = useFileCollectionsFromParentId(activeCollectionId);
  const files = useFilesFromFileCollectionId(activeCollectionId);
  const hasFolders = !isEmpty(collections);
  const hasFiles = !isEmpty(files);
  const showSectionLabels = hasFolders && hasFiles;

  return (
    <Tab name={name} label={label}>
      <div className={classes.header}>
        <FileCollectionCrumbs
          condensed
          showAtHome={false}
          homeValue={baseCollectionId}
          crumbs={crumbs}
          onSelect={onSelectCrumb}
        />
      </div>
      <div className={classes.body}>
        {hasFolders && (
          <div>
            {showSectionLabels && (
              <div className={classes.sectionLabel}>Folders</div>
            )}
            <div className={classes.list}>
              {collections.map((c) => (
                <FileCollectionItem
                  key={c.id}
                  fileCollectionId={c.id}
                  variant="aurora"
                  onViewFileCollection={onSelectCollection}
                  onOpenFileCollection={onSelectCollection}
                />
              ))}
            </div>
          </div>
        )}
        {hasFiles && (
          <div>
            {showSectionLabels && (
              <div className={classes.sectionLabel}>Files</div>
            )}
            <div className={classes.list}>
              {sortByCreatedAt(files).map((f) => (
                <FileItem
                  key={f.id}
                  active={activeFileId === f.id}
                  fileId={f.id}
                  variant="aurora"
                  onSelect={onSelectFile}
                />
              ))}
            </div>
          </div>
        )}
        {!hasFolders && !hasFiles && (
          <div className={classes.empty}>This folder is empty</div>
        )}
      </div>
    </Tab>
  );
}
